import { useState } from 'react'
import { XSquare, ArrowRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { formatDate } from '../../utils/interviews'
import type { PendingCorrection } from './reconcile'

// Batch close for corrections that should be taken out of the pending list
// without being applied. A single reason is required and applied to every
// selected row. Lists the selected corrections compactly so the reviewer knows
// what they are closing.
export function BatchCloseDialog({
  open,
  onOpenChange,
  rows,
  loading,
  error,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (next: boolean) => void
  rows: PendingCorrection[]
  loading: boolean
  error: string | null
  onConfirm: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Close {rows.length} correction{rows.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription>
            The selected proposals are taken out of the pending list. Nothing about the shifts
            changes. The reason below is recorded against every one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="max-h-64 overflow-y-auto rounded-md border border-border">
            <ul className="divide-y divide-border">
              {rows.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                  <span className="w-24 shrink-0 font-medium text-foreground">
                    {formatDate(r.shift_date)}
                  </span>
                  <span className="flex flex-1 items-center gap-1.5 font-mono text-xs text-foreground">
                    <span>{r.from_value || '—'}</span>
                    <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span>{r.to_value || '—'}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="batch-close-reason" className="text-xs font-medium text-muted-foreground">
              Reason
            </Label>
            <Textarea
              id="batch-close-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are these being closed? Applied to every selected correction."
              rows={3}
              className="text-sm"
            />
          </div>

          {error ? (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => onConfirm(trimmed)}
            disabled={loading || trimmed.length === 0 || rows.length === 0}
          >
            {loading ? <Spinner className="mr-1.5 h-4 w-4" /> : <XSquare className="mr-1.5 h-4 w-4" />}
            Close {rows.length} correction{rows.length === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
